import { Container, Graphics, Sprite, Text, TextStyle } from 'pixi.js';
import { getTexture, makeSprite } from '@/assets/resolver';
import { COLORS, WEAPON_CATEGORY_COLOR } from '@/config/colors';
import { HUD } from '@/config/hud';
import { settings } from '@/config/settings';
import { WEAPONS } from '@/config/weapons';
import { WEAPON_RARITY_COLOR, weaponCrateKey } from '@/config/weapon-visuals';
import { t } from '@/i18n';
import { arrowGlyph, bagGlyph, sparkleGlyph, swapGlyph } from '@/ui/hangar-icons';
import type { CrateDestination } from '@/ui/shop-types';
import { scrapGems, weaponName, weaponRarityName } from '@/ui/weapon-labels';
import { chamferPanel, hexPath, UI_FONT } from '@/ui/widgets';

interface WeaponDrop {
  weaponId: string;
  x: number;
  y: number;
  sprite: Sprite;
  bobPhase: number;
  /** Despawn countdown (docs/12 M7) — blinks near the end */
  ttl: number;
}

const PICKUP_RADIUS = 46;
const TTL = 18;
const BLINK_TIME = 4;
const LABEL = HUD.crateLabel;

/**
 * Weapon crate drops. Each crate shows the weapon's OWN glyph + rarity rim;
 * the nearest crate within reach floats a WORDLESS preview of where it will
 * go — → numbered socket / bag / full bag (swap at the break) / +N gems —
 * with the name + rarity under it (docs/09 wave 4). Pickups never swap an
 * equipped weapon.
 */
export class DropSystem {
  readonly view = new Container();
  private drops: WeaponDrop[] = [];
  private time = 0;
  /** Where a crate would land if picked up now (run/hangar.ts sets it) */
  destinationOf: ((weaponId: string) => CrateDestination) | null = null;
  /** Socket index an EMPTY-socket pickup would fill */
  nextSocket: (() => number) | null = null;
  /** One shared tag, redrawn only when the crate / destination / language changes */
  private readonly tag = new Container();
  private readonly marks = new Graphics();
  private readonly gem: Sprite;
  private readonly digit: Text;
  private readonly label: Text;
  /** What the tag shows now (field compare — no per-frame strings) */
  private tagFor: string | null = null;
  private tagDest: CrateDestination | null = null;
  private tagLang = '';

  constructor() {
    const style = (size: number, fill: number) => new TextStyle({
      fontFamily: UI_FONT, fontSize: size, fill, letterSpacing: 2,
      stroke: { color: COLORS.outline, width: 4 },
    });
    this.label = new Text({ text: '', style: style(LABEL.fontSize, 0xffffff) });
    this.label.anchor.set(0.5, 0);
    this.label.position.set(0, 4);
    this.digit = new Text({ text: '', style: style(LABEL.digitSize, 0x0f0c1a) });
    this.digit.anchor.set(0.5);
    this.gem = makeSprite('pickup/gem-yellow');
    this.gem.anchor.set(0.5);
    this.tag.addChild(this.marks, this.gem, this.digit, this.label);
    this.tag.visible = false;
    this.view.addChild(this.tag);
  }

  spawn(weaponId: string, x: number, y: number): void {
    if (!WEAPONS[weaponId]) return;
    const sprite = makeSprite(weaponCrateKey(weaponId));
    sprite.position.set(x, y);
    this.view.addChildAt(sprite, 0); // label stays on top
    this.drops.push({ weaponId, x, y, sprite, bobPhase: this.drops.length * 1.3, ttl: TTL });
  }

  /** Check player contact; returns picked weapon id (crate is consumed). */
  tryPickup(px: number, py: number): string | null {
    for (let i = 0; i < this.drops.length; i++) {
      const d = this.drops[i]!;
      if (Math.hypot(px - d.x, py - d.y) < PICKUP_RADIUS) {
        d.sprite.destroy();
        this.drops.splice(i, 1);
        return d.weaponId;
      }
    }
    return null;
  }

  update(dt: number): void {
    this.time += dt;
    for (let i = this.drops.length - 1; i >= 0; i--) {
      const d = this.drops[i]!;
      d.ttl -= dt;
      if (d.ttl <= 0) {
        d.sprite.destroy();
        this.drops.splice(i, 1);
      }
    }
  }

  /** viewer = player position: drives the floating name label. */
  render(viewerX: number, viewerY: number): void {
    let nearest: WeaponDrop | null = null;
    let best: number = LABEL.radius;
    for (const d of this.drops) {
      d.sprite.y = d.y + Math.sin(this.time * 2.4 + d.bobPhase) * 5;
      d.sprite.rotation = Math.sin(this.time * 1.1 + d.bobPhase) * 0.1;
      d.sprite.alpha = d.ttl < BLINK_TIME ? (Math.sin(this.time * 14) > 0 ? 1 : 0.25) : 1;
      const dist = Math.hypot(viewerX - d.x, viewerY - d.y);
      if (dist < best) {
        best = dist;
        nearest = d;
      }
    }
    this.tag.visible = nearest !== null;
    if (!nearest) return;
    const dest = this.destinationOf?.(nearest.weaponId) ?? 'bag';
    if (nearest.weaponId !== this.tagFor || dest !== this.tagDest || settings.language !== this.tagLang) {
      this.tagFor = nearest.weaponId;
      this.tagDest = dest;
      this.tagLang = settings.language;
      this.drawTag(nearest.weaponId, dest);
    }
    this.tag.position.set(nearest.x, nearest.sprite.y + LABEL.offsetY);
    this.tag.alpha = nearest.sprite.alpha;
  }

  /** "→ [destination]" bubble + the name line under it (secondary text). */
  private drawTag(weaponId: string, dest: CrateDestination): void {
    const def = WEAPONS[weaponId]!;
    const cat = WEAPON_CATEGORY_COLOR[def.category];
    const { bubbleW: bw, bubbleH: bh } = LABEL;
    // Bubble sits above the tag origin; marks draw in bubble-local space
    const m = this.marks.clear();
    m.position.set(-bw / 2, -bh);
    chamferPanel(m, bw, bh, {
      fill: 0x14101f, fillAlpha: 0.88, stroke: dest === 'full' ? COLORS.dangerOrange : cat,
      strokeWidth: 3, notch: 8, plain: true,
    });
    const cx = bw / 2;
    const cy = bh / 2;
    const ix = cx + 16;
    arrowGlyph(m, cx - 20, cy, 10, 0xe8eaf0);
    this.gem.visible = false;
    this.digit.text = '';
    this.digit.style.fill = 0x0f0c1a;
    this.digit.position.set(ix - bw / 2, cy - bh + 1);
    if (dest === 'socket') {
      hexPath(m, ix, cy, 14).fill(cat);
      this.digit.text = String((this.nextSocket?.() ?? 0) + 1);
    } else if (dest === 'bag') {
      bagGlyph(m, ix - 2, cy + 2, 12, COLORS.playerCream);
      sparkleGlyph(m, ix + 12, cy - 11, 5, COLORS.playerYellow);
    } else if (dest === 'full') {
      bagGlyph(m, ix - 6, cy + 2, 11, COLORS.dangerOrange);
      swapGlyph(m, ix + 12, cy - 9, 6, COLORS.playerYellow);
    } else {
      this.gem.texture = getTexture('pickup/gem-yellow');
      this.gem.visible = true;
      this.gem.scale.set(0.5);
      this.gem.position.set(ix - 12 - bw / 2, cy - bh);
      this.digit.text = `+${scrapGems(def.id)}`;
      this.digit.style.fill = COLORS.gemYellow;
      this.digit.position.set(ix + 10 - bw / 2, cy - bh);
    }
    this.label.text = t('wstat.crateLabel', {
      name: weaponName(def.id), rarity: weaponRarityName(def.id),
    });
    this.label.style.fill = def.rarity === 'common' ? COLORS.playerCream : WEAPON_RARITY_COLOR[def.rarity];
  }

  clear(): void {
    for (const d of this.drops) d.sprite.destroy();
    this.drops = [];
    this.tag.visible = false;
    this.tagFor = null;
  }
}
